'use client'
import { EmptyState } from '@/features/shop/components/EmptyState'
import { DealsLoadingState } from '@/features/shop/components/Deals/DealsLoadingState'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { Display } from '@shipt/design-system-typography'
import { Row } from '@shipt/design-system-layouts'
import { useCMSParams, CMS_DEALS_SLUG, useQueryCMS } from '@/features/cms/services/CMS/queries'
import { NoDataState } from '@/features/cms/components/CMS/components/Layouts/NoDataState'
import { LayoutRenderer } from '@/features/cms/components/CMS/components/Layouts'
import { useParams } from 'next/navigation'
import { type ParamsOf } from '@next-types/routes'
import { SEOHead } from '@/features/shared/components/SEOHeader/SEOHead'
import { useUserStoreName } from '@/features/account/services/User/hooks'
import { getDealsPageSeo } from '@/features/shop/components/Deals/dealsSeo'

const DealsShelvesWithCMS = () => {
  const params = useCMSParams(CMS_DEALS_SLUG)
  const {
    data: cmsShelves,
    isPending: isCMSShelvesPending,
    error,
  } = useQueryCMS({
    params,
    enabled: Boolean(params.storeParams.store_location_id),
  })

  if (isCMSShelvesPending) return <DealsLoadingState />

  if (error) return <NoDataState />

  if (
    !cmsShelves.content.data ||
    !('body' in cmsShelves.content.data) ||
    !cmsShelves.content.data.body
  ) {
    return <EmptyState text="Nothing on sale today" subText="New deals will appear here." />
  }

  return <LayoutRenderer layoutData={cmsShelves.content} />
}

export const DealsPage = () => {
  const params = useParams<ParamsOf<'/shop/[store_slug]'>>()
  const storeName = useUserStoreName()
  const { title, description, canonicalUrl, breadcrumbs, pageTypeSchema } = getDealsPageSeo(
    params?.store_slug,
    storeName,
  )

  return (
    <>
      <SEOHead
        title={title}
        description={description}
        canonicalUrl={canonicalUrl}
        breadcrumbs={breadcrumbs}
        pageTypeSchema={pageTypeSchema}
      />
      <GridLayoutContainer>
        <Row margin={{ base: ['xxl', 0, 'lg'], md: ['lg', 0, '3xl'] }} paddingTop="xxl">
          <Display size="sm">Deals</Display>
        </Row>
      </GridLayoutContainer>
      <DealsShelvesWithCMS />
    </>
  )
}
